import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Pen,
  Trash2,
} from "lucide-react";
import { useSearch } from "../../SearchContext";
import { useSidebar } from "../../SidebarContext";
import Loader from "../Loader";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";




const TeamDirectory = () => {

  const { globalSearch } = useSearch();
  const [currentPage, setCurrentPage] = useState(1);
  const [load, setLoad] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const token = decryptToken("accessToken") || ""
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const apiUrl = API_BASE_URL;



  // Helper function to format dates
  const safeDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "—";

  // 🔍 Filtering only by search
  useEffect(() => {
    let filtered = [...employees];
    if (globalSearch) {
      filtered = filtered.filter(
        (employee) =>
          employee.name.toLowerCase().includes(globalSearch.toLowerCase()) ||
          employee.department
            .toLowerCase()
            .includes(globalSearch.toLowerCase()) ||
          employee.employeeId
            .toLowerCase()
            .includes(globalSearch.toLowerCase()) ||
          employee.jobTitle.toLowerCase().includes(globalSearch.toLowerCase())
      );
    }
    setFilteredEmployees(filtered);
  }, [employees, globalSearch]);




  useEffect(() => {
    const fetchEmployees = async () => {
      setLoad(true);
      try {
        const res = await fetch(`${apiUrl}/staff`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,

          }
        }); // 🔑 Add param if needed
        if (!res.ok) throw new Error("Failed to fetch employees");

        const data = await res.json();
        console.log("📥 Employees:", data);

        setEmployees(data);
        setFilteredEmployees(data);
      } catch (error) {
        console.error("❌ Error fetching employees:", error);
        toast.error("Failed to load employees.");
      } finally {
        setLoad(false);
      }
    };

    fetchEmployees();
  }, []);

  // Pagination
  const entriesPerPage = 5; // fixed since filters removed
  const totalPages = Math.ceil(filteredEmployees.length / entriesPerPage);
  const currentData = filteredEmployees.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);



  if (load) {
    return <Loader />;
  }

  const handleDelete = async (employeeId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${apiUrl}/staff/${employeeId}`, {
            method: "DELETE",
            headers: {
              "Authorization": `Bearer ${token}`, // Do NOT set Content-Type, browser will set it
            },
          });

          const data = await res.json();

          if (!res.ok || data.isError) {
            throw new Error(data.message || "Failed to delete employee");
          }

          // ✅ Remove from local state only if backend deletion succeeds
          setEmployees((prev) => prev.filter((item) => item.id !== employeeId));
          setFilteredEmployees((prev) =>
            prev.filter((item) => item.id !== employeeId)
          );

          toast.success("Employee has been deleted.");
        } catch (error) {
          console.error("❌ Delete error:", error);
          toast.error("Failed to delete employee.");
        }
      }
    });
  };


  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Team Directory</h1>
        <div className="customer-warning-new-hamburger-help">
          {selectedIds.length > 0 && (
            <button
              className="oms-add-btn"
              style={{ backgroundColor: "red", marginLeft: "10px" }}
              onClick={() => {
                Swal.fire({
                  title: "Delete selected?",
                  text: `This will delete ${selectedIds.length} employees.`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete them!",
                }).then((result) => {
                  if (result.isConfirmed) {
                    setEmployees((prev) =>
                      prev.filter((item) => !selectedIds.includes(item.id))
                    );
                    setFilteredEmployees((prev) =>
                      prev.filter((item) => !selectedIds.includes(item.id))
                    );
                    setSelectedIds([]);
                    toast.success("Selected employees deleted.");
                  }
                });
              }}
            >
              Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Employee Table */}
      <div className="customer-table responsive-table">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    currentData.every((c) => selectedIds.includes(c.id)) &&
                    currentData.length > 0
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newIds = currentData
                        .map((c) => c.id)
                        .filter((id) => !selectedIds.includes(id));
                      setSelectedIds([...selectedIds, ...newIds]);
                    } else {
                      const newIds = selectedIds.filter(
                        (id) => !currentData.map((c) => c.id).includes(id)
                      );
                      setSelectedIds(newIds);
                    }
                  }}
                />
              </th>
              <th>DOB</th>
              <th>Name</th>
              <th>Gender</th>
              <th>Department</th>
              <th>Employee ID</th>
              <th>Mobile</th>
              <th>Job Title</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((employee) => (
                <tr key={employee.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(employee.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds((prev) => [...prev, employee.id]);
                        } else {
                          setSelectedIds((prev) =>
                            prev.filter((id) => id !== employee.id)
                          );
                        }
                      }}
                    />
                  </td>
                  <td>{safeDate(employee.dob)}</td>
                  <td>{`${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "N/A"}</td>
                  <td>{employee.gender || "N/A"}</td>
                  <td>
                    {(() => {
                      try {
                        const dep = JSON.parse(employee.department); // try to parse
                        return Array.isArray(dep) ? dep.join(", ") : dep;
                      } catch {
                        return employee.department || "N/A"; // fallback if not JSON
                      }
                    })()}
                  </td>


                  <td>{employee.emplyee_ID || "N/A"}</td>
                  <td>{employee.phone || "N/A"}</td>
                  <td>{employee.job_title || "N/A"}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        onClick={() => navigate("/new-staff", { state: { ...employee } })}
                      >
                        <Pen size={18} />
                      </button>


                      <button
                        onClick={() => handleDelete(employee.id)}
                        className="delete-btn"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  No Employees found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Responsive Cards */}
      <div className="ecom-responsive-cards">
        {currentData.length === 0 ? (
          <p style={{ textAlign: "center", margin: "50px" }}>
            No Employees Available
          </p>
        ) : (
          currentData.map((employee, index) => (
            <div
              className="ecom-responsive-card"
              key={index}
              onClick={() => navigate(`/employee-detail/${employee.id}`)}
              style={{ cursor: "pointer" }}
            >
              <div className="ecom-responsive-header">
                <p>{employee.emplyee_ID || "—"}</p>
                <p>{safeDate(employee.dob)}</p>
              </div>
              <div className="ecom-responsive-body">
                <div className="ecom-responsive-left">
                  <h2>{`${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "N/A"}</h2>
                  <p>{(() => {
                    try {
                      const dep = JSON.parse(employee.department); // try to parse
                      return Array.isArray(dep) ? dep.join(", ") : dep;
                    } catch {
                      return employee.department || "N/A"; // fallback if not JSON
                    }
                  })()}</p>
                </div>
                <div className="ecom-responsive-right">
                  <h4>{employee.job_title || "No Job Title"}</h4>
                  <p>{employee.phone || "No Mobile"}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="pagination-chev-btn"
          >
            <ChevronsLeft size={18} />
          </button>
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-chev-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <button className="pagination-btn active">{currentPage}</button>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-chev-btn"
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-chev-btn"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      )}

      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button
          onClick={() => navigate("/new-staff")}
          className="cssbuttons-io-button">
          <span>Add Employee</span>
        </button>
      </div>
    </>
  );
};

export default TeamDirectory;
